import { useCallback, useMemo, useState } from 'react';
import {
  ModelRuns,
  getModelRunsTableColumns,
} from '@elementary/common-ui/pages';
import { DEFAULT_DAYS_BACK } from '@elementary/common-ui/constants';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useAiChatPanel from '@elementary/common-ui/hooks/useAiChatPanel';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import { ModelRuns as ModelRunsType } from '@elementary/common-ui/types';
import useModelRuns from './hooks/useModelRuns';
import useGroupedModelRuns from './hooks/useGroupedModelRuns';
import ModelRunsExpandedRow from './components/ModelRunsExpandedRow';

const ModelRunsPage = () => {
  const { data: account } = useAccount();
  const { openPanel } = useAiChatPanel();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const grouped = useGroupedModelRuns({ search: debouncedSearch });
  const { isGrouped } = grouped;
  const {
    tableData: flatTableData,
    isTableAssetsFetching,
    fetchNextPage,
    hasNextPage,
    filterOptions,
    isFilterOptionsFetching,
  } = useModelRuns({ search: debouncedSearch, enabled: !isGrouped });

  const tableData = isGrouped ? grouped.tableData : flatTableData;

  const columns = useMemo(
    () => getModelRunsTableColumns(tableData, filterOptions),
    [tableData, filterOptions],
  );

  const onScrollToBottom = useCallback(() => {
    if (fetchNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const handleOpenAiChat = useCallback(
    (rowData: ModelRunsType) => {
      openPanel({
        startMessage: `How can I optimize ${rowData.unique_id}?`,
        context: { assetIds: [rowData.unique_id] },
      });
    },
    [openPanel],
  );

  return (
    <>
      {grouped.groupFetchers}
      <ModelRuns
        tableData={tableData}
        columns={columns}
        isLoading={isGrouped ? grouped.isFetching : isTableAssetsFetching}
        search={search}
        onSearch={setSearch}
        daysBack={DEFAULT_DAYS_BACK}
        isControlled
        isFilterOptionsLoading={isFilterOptionsFetching}
        onScrollToBottom={onScrollToBottom}
        bottomScrollPadding={100}
        showFloatingActionsMenu
        pageTitle="Task Performance"
        groupBy={grouped.groupBy}
        onGroupByChange={grouped.onGroupByChange}
        groupableColumnIds={grouped.groupableColumnIds}
        groupedData={isGrouped ? grouped.groupedData : undefined}
        expandedGroups={isGrouped ? grouped.expandedGroups : undefined}
        onGroupToggle={isGrouped ? grouped.onGroupToggle : undefined}
        onGroupScrollToBottom={
          isGrouped ? grouped.onGroupScrollToBottom : undefined
        }
        expandedRowRenderer={(rowData) => (
          <ModelRunsExpandedRow
            rowData={rowData}
            onOpenAiChat={handleOpenAiChat}
            aiOptimizationEnabled={!!account?.ai_features_enabled}
          />
        )}
      />
    </>
  );
};

export default ModelRunsPage;
